import * as THREE from "three";
import { GAME } from "../config/constants.js";
import { angleLerp } from "../core/util.js";
import { fitToHeight } from "../core/modelFit.js";
import { CharacterAnimator } from "./CharacterAnimator.js";
import { starterKit } from "../items/ItemFactory.js";
import { memWeaponMult, memArmorBonus, ensureMemory } from "../systems/RelicMemory.js";
import { affinityFor } from "../config/relics.js";

// If a loaded character faces the wrong way, set this to Math.PI.
export const MODEL_FACE_OFFSET = 0;
// Orientation offset applied to a weapon sitting in the hand slot (tune if a
// blade points the wrong way).
const WEAPON_GRIP_ROT = new THREE.Euler(0, 0, 0);
// Per-model grip corrections: some packs author a weapon in a different rest
// pose. [regex on model key, [x,y,z] radians]. Use the localhost weapon-grip
// tuner ([ ] ; ' , . keys) to find the right values, then bake them here.
const GRIP_BY_MODEL = [
  [/legend\/axe/, [-0.2618, -3.4034, 0]],   // greataxe: blade to the ground (tuned)
];
const SWING_TIME = 0.16;
// How long a food buff lasts in a slot before it wears off (seconds).
export const FOOD_DURATION = 75;

/**
 * Player: camera-relative movement, smooth turning, dodge-roll, and attack.
 * Uses a real character model (auto-fit + idle/walk animation) with the
 * equipped weapon model parented to the hand bone; melee attacks play a
 * procedural weapon swing (the free KayKit set has no melee clip).
 */
export class Player {
  constructor(scene, registry, level, modelKey, classId = "knight", charLevel = 1) {
    this.scene = scene;
    this.registry = registry;
    this.modelKey = modelKey;
    this.classId = classId;
    this.level = charLevel;       // meta character level (from the save profile)
    this.powerMult = 1;

    this.pos = new THREE.Vector3();
    this.radius = 0.5;
    this.groundH = 0;      // eased floor height under the player (stairs/stories)
    this.yaw = 0;
    this.desiredYaw = 0;

    this.baseHp = GAME.playerBaseHp;
    this.rmods = {};          // research modifiers (dmg/hp/stam/mana/crit/speed), set by Game
    this.speedMult = 1;
    this.critChance = 0;
    const kit = starterKit(classId);
    this.weapon = kit.weapon;
    this.armor = kit.armor;
    this.classMana = kit.maxMana || 0;
    this.foods = [null, null, null];             // 3 food slots (temporary stacking buffs)
    this.foodTimers = [0, 0, 0];                  // seconds remaining before each buff expires
    this.buffs = { stamRegen: 0, manaRegen: 0, maxStam: 0, maxMana: 0 };
    this.invCap = GAME.invCap;
    this.inv = new Array(GAME.invCap).fill(null);   // FIXED slots with null gaps
    this.gold = 0;
    this.keys = 0;   // Vault Keys — currency for locked chests & vault doors
    this.applyGear();
    this.hp = this.maxHp;
    this.stamina = this.maxStamina;
    this.mana = this.maxMana;

    this.atkCd = 0;
    this.abilityCd = 0;        // signature-ability cooldown remaining
    this.abilityCdMax = 0;     // last cooldown length (for the UI sweep)
    this.shieldTimer = 0;      // knight Bulwark: halves incoming damage while > 0
    this.hasteTimer = 0;       // rogue Smoke: move-speed boost while > 0
    this.whirl = null;         // barbarian Whirlwind channel state {t, tick}
    this.rollTimer = 0;
    this.rollCd = 0;
    this.rollDir = new THREE.Vector3();
    this.hurt = 0;
    this.swing = 0;
    this.blocking = false;
    this.blockTimer = 0;

    this._buildMesh();
    this.spawnAt(level);
  }

  applyGear() {
    ensureMemory(this.weapon, this.classId); ensureMemory(this.armor, this.classId);
    const lv = (this.level || 1) - 1;
    const memHp = this.armor ? memArmorBonus(this.armor).hp : 0;   // awakened-armor bonus HP
    const rm = this.rmods || {};
    this.maxHp = this.baseHp + (this.armor ? this.armor.hp : 0) + memHp + lv * 8 + (rm.hp || 0);
    if (this.hp !== undefined) this.hp = Math.min(this.hp, this.maxHp);
    this._recomputeBuffs();
  }

  /** Total offensive multiplier: character level × awakened memory × relic affinity. */
  weaponPower() {
    let m = this.powerMult || 1;
    m *= memWeaponMult(this.weapon);
    m *= affinityFor(this.weapon, this.classId, this.attuneAny).mult;
    return m;
  }

  /** Recompute food buffs + character-level bonuses → max stamina/mana + power. */
  _recomputeBuffs() {
    const b = { stamRegen: 0, manaRegen: 0, maxStam: 0, maxMana: 0 };
    for (const f of this.foods || []) if (f && f.buffs) for (const k in f.buffs) b[k] = (b[k] || 0) + f.buffs[k];
    this.buffs = b;
    const lv = (this.level || 1) - 1;
    const rm = this.rmods || {};
    this.maxStamina = GAME.playerMaxStamina + b.maxStam + lv * 4 + (rm.stam || 0);
    this.maxMana = (this.classMana || 0) + b.maxMana + lv * 3 + (rm.mana || 0);
    this.powerMult = (1 + lv * 0.05) * (1 + (rm.dmg || 0));   // +5%/level × research damage
    this.speedMult = 1 + (rm.speed || 0);
    this.critChance = rm.crit || 0;
    // Legendary Memory Relic passive buffs (special effects while equipped)
    const w = this.weapon || {};
    this.speedMult += (w.moveBonus || 0);
    this.critChance += (w.critBonus || 0);
    this.lifesteal = w.lifesteal || 0;
    if (this.stamina !== undefined) this.stamina = Math.min(this.stamina, this.maxStamina);
    if (this.mana !== undefined) this.mana = Math.min(this.mana, this.maxMana);
  }

  /** Set the meta character level (on level-up mid-run) and rescale stats. */
  setLevel(l) { this.level = l; this.applyGear(); }

  /** Slot a food (0-2), applying its temporary buff. Returns the displaced food. */
  setFood(idx, item) {
    if (idx < 0 || idx > 2) return null;
    const old = this.foods[idx];
    this.foods[idx] = item;
    if (!this.foodTimers) this.foodTimers = [0, 0, 0];
    this.foodTimers[idx] = item ? FOOD_DURATION : 0;
    this._recomputeBuffs();
    return old;
  }

  /** Tick down food-buff timers; a slot clears itself when its buff expires. */
  _updateFood(dt) {
    if (!this.foodTimers) return;
    for (let i = 0; i < 3; i++) {
      if (!this.foods[i]) continue;
      this.foodTimers[i] -= dt;
      if (this.foodTimers[i] <= 0) { this.foods[i] = null; this.foodTimers[i] = 0; this._recomputeBuffs(); }
    }
  }

  /** True only when there's an empty food slot — food can't be eaten otherwise. */
  hasFoodSlot() { return this.foods.some((f) => !f); }

  _buildMesh() {
    this.mesh = new THREE.Group();
    if (this.registry.has(this.modelKey)) {
      const model = this.registry.instance(this.modelKey);
      fitToHeight(model, 1.9);
      model.rotation.y = MODEL_FACE_OFFSET;
      model.traverse((o) => { if (o.isMesh) o.castShadow = true; });
      this.mesh.add(model);

      // weapon mount on the right hand bone. NOTE: three's GLTFLoader strips
      // dots from node names, so KayKit's "handslot.r" becomes "handslotr".
      this.handSlot =
        model.getObjectByName("handslotr") || model.getObjectByName("handr") ||
        model.getObjectByName("handslot.r") || model.getObjectByName("hand.r");
      if (this.handSlot) {
        this.weaponMount = new THREE.Group();
        this.handSlot.add(this.weaponMount);
      }
      // shield on the off-hand, shown only while blocking
      this.handSlotL = model.getObjectByName("handslotl") || model.getObjectByName("handl");
      if (this.handSlotL && this.registry.has("weapon/shield")) {
        this.shield = this.registry.instance("weapon/shield");
        this.shield.visible = false;
        this.shield.traverse((o) => { if (o.isMesh) o.castShadow = true; });
        this.handSlotL.add(this.shield);
      }

      const clips = [
        ...(model.animations || []),
        ...this.registry.clips("anim/kaykit_general"),
        ...this.registry.clips("anim/kaykit_movement"),
      ];
      if (clips.length) this.animator = new CharacterAnimator(model, clips);
      this._attachWeapon();
    } else {
      this.mesh.add(this._placeholder());
    }

    // belt lantern + its warm light — a carried light source for dark dungeons
    if (this.registry.has("gear/lantern")) {
      this.lantern = this.registry.instance("gear/lantern");
      this.lantern.scale.setScalar(0.5);
      this.lantern.position.set(0.42, 0.85, 0.12);   // hangs at the right hip, slightly forward
      this.lantern.traverse((o) => { if (o.isMesh) o.castShadow = false; });
      this.mesh.add(this.lantern);
    }
    this.lanternLight = new THREE.PointLight(0xffb867, 2.6, 13, 1.5);
    this.lanternLight.position.set(0.42, 1.2, 0.12);
    this.mesh.add(this.lanternLight);

    this.scene.add(this.mesh);
  }

  _attachWeapon() {
    if (!this.weaponMount) return;
    while (this.weaponMount.children.length) this.weaponMount.remove(this.weaponMount.children[0]);
    const key = this.weapon && this.weapon.model;
    if (key && this.registry.has(key)) {
      const w = this.registry.instance(key);
      // grip orientation: per-model correction, or a per-weapon `grip` override
      let grip = WEAPON_GRIP_ROT;
      for (const [re, e] of GRIP_BY_MODEL) { if (re.test(key)) { grip = new THREE.Euler(e[0], e[1], e[2]); break; } }
      if (Array.isArray(this.weapon.grip)) grip = new THREE.Euler(this.weapon.grip[0], this.weapon.grip[1], this.weapon.grip[2]);
      w.rotation.copy(grip);
      this.heldWeapon = w;   // exposed for the localhost weapon-grip tuner
      // optional gentle glow (e.g. gift relics like Todd) — clone materials so we
      // only tint this copy, never the shared model in the registry
      const glow = this.weapon.glow ? new THREE.Color(this.weapon.glow) : null;
      w.traverse((o) => {
        if (!o.isMesh) return;
        o.castShadow = true;
        if (glow) {
          o.material = Array.isArray(o.material) ? o.material.map((m) => m.clone()) : o.material.clone();
          (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => { if ("emissive" in m) { m.emissive = glow; m.emissiveIntensity = 0.35; } });
        }
      });
      this.weaponMount.add(w);
    }
  }

  _placeholder() {
    const g = new THREE.Group();
    this._bodyMat = new THREE.MeshStandardMaterial({ color: this.armor.color, roughness: 0.7 });
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.9, 1.1, 0.7), this._bodyMat);
    body.position.y = 0.9; body.castShadow = true;
    const head = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.6, 0.6),
      new THREE.MeshStandardMaterial({ color: 0xe8c39e, roughness: 0.6 }));
    head.position.y = 1.75; head.castShadow = true;
    const visor = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.16, 0.1),
      new THREE.MeshStandardMaterial({ color: 0x222222 }));
    visor.position.set(0, 1.75, 0.33);
    this.weaponNub = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 1.0),
      new THREE.MeshStandardMaterial({ color: this.weapon.color }));
    this.weaponNub.position.set(0.55, 0.95, 0.3);
    g.add(body, head, visor, this.weaponNub);
    return g;
  }

  spawnAt(level) {
    const w = level.playerStartWorld();
    this.groundH = level.groundY ? level.groundY(w.x, w.z) : 0;
    this.pos.set(w.x, this.groundH, w.z);
    this.mesh.position.copy(this.pos);
  }

  /** Called after gear changes (equip): refresh placeholder tint + hand weapon. */
  refreshPlaceholderColors() {
    if (this._bodyMat) this._bodyMat.color.set(this.armor.color);
    if (this.weaponNub) this.weaponNub.material.color.set(this.weapon.color);
    this._attachWeapon();
  }

  update(dt, input, cameraRig, combat, level) {
    this.atkCd = Math.max(0, this.atkCd - dt);
    this.abilityCd = Math.max(0, this.abilityCd - dt);
    this.shieldTimer = Math.max(0, (this.shieldTimer || 0) - dt);
    this.hasteTimer = Math.max(0, (this.hasteTimer || 0) - dt);
    this.rollCd = Math.max(0, this.rollCd - dt);
    this.invuln = Math.max(0, (this.invuln || 0) - dt);
    this.webRoot = Math.max(0, (this.webRoot || 0) - dt);   // spider web hold
    this._updateFood(dt);                                    // food buffs expire over time
    this.hurt = Math.max(0, this.hurt - dt);
    this.swing = Math.max(0, this.swing - dt);
    if (this.animator) this.animator.update(dt);

    // stamina + mana regen
    this.stamina = Math.min(this.maxStamina, this.stamina + (GAME.staminaRegen + this.buffs.stamRegen) * dt);
    this.mana = Math.min(this.maxMana, this.mana + (GAME.manaRegen + this.buffs.manaRegen + ((this.rmods && this.rmods.manaRegen) || 0)) * dt);

    const f = cameraRig.forward, r = cameraRig.right;
    const k = input.keys;
    const fwd = (k["w"] ? 1 : 0) - (k["s"] ? 1 : 0);
    const strafe = (k["d"] ? 1 : 0) - (k["a"] ? 1 : 0);
    let mx = f.x * fwd + r.x * strafe;
    let mz = f.z * fwd + r.z * strafe;
    const moveLen = Math.hypot(mx, mz);
    if (moveLen > 0) { mx /= moveLen; mz /= moveLen; }

    let speed = GAME.playerSpeed * (this.speedMult || 1);
    if (this.hasteTimer > 0) speed *= 1.5;          // Smoke Bomb: fleet-footed
    if (this.whirl) speed *= 0.55;                   // Whirlwind: slowed while spinning
    if (this.rollTimer > 0) {
      this.rollTimer -= dt;
      mx = this.rollDir.x; mz = this.rollDir.z;
      speed = GAME.rollSpeed;
    } else if (k["shift"] && this.rollCd <= 0 && moveLen > 0 && this.stamina >= GAME.rollStamina) {
      this.rollTimer = GAME.rollTime;
      this.rollCd = GAME.rollCooldown;
      this.rollDir.set(mx, 0, mz);
      this.stamina -= GAME.rollStamina;   // dodge costs stamina
      this.webRoot = 0;                    // a dodge-roll tears you free of any web
    }

    // block (RMB): raise shield, slow down, face the cursor; drains stamina
    const wantBlock = input.rightDown && this.rollTimer <= 0 && this.stamina > 1;
    if (wantBlock && !this.blocking) this.blockTimer = 0;
    this.blocking = wantBlock;
    if (this.blocking) { this.blockTimer += dt; speed *= 0.42; this.stamina = Math.max(0, this.stamina - GAME.blockStamina * dt); }
    if (this.shield) this.shield.visible = this.blocking;

    // caught in a web: rooted for a split second (a dodge-roll still frees you)
    if (this.webRoot > 0 && this.rollTimer <= 0) speed = 0;

    if (mx !== 0 || mz !== 0) {
      const nx = this.pos.x + mx * speed * dt;
      const nz = this.pos.z + mz * speed * dt;
      if (!level.collidesWorld(nx, this.pos.z, this.radius)) this.pos.x = nx;
      if (!level.collidesWorld(this.pos.x, nz, this.radius)) this.pos.z = nz;
      if (this.rollTimer <= 0) this.desiredYaw = Math.atan2(mx, mz);
    }
    this.moving = moveLen > 0 || this.rollTimer > 0;   // for footstep cadence / SFX
    if (this.animator) this.animator.setMoving(this.moving);

    const aim = input.groundPoint(cameraRig.camera);
    const aimYaw = Math.atan2(aim.x - this.pos.x, aim.z - this.pos.z);
    if (this.blocking) this.desiredYaw = aimYaw;
    if ((input.mouseDown || k[" "]) && this.atkCd <= 0 && this.rollTimer <= 0 && !this.blocking) {
      const magic = this.weapon.magic;
      if (!magic || this.mana >= GAME.magicManaCost) {
        if (magic) this.mana -= GAME.magicManaCost;   // casting costs mana
        this.desiredYaw = aimYaw;
        this.atkCd = this.weapon.cd;
        this.swing = SWING_TIME;
        combat.playerAttack(this, aimYaw);
      } else {
        this.atkCd = 0.25;            // out of mana — brief pause, no cast
        this.outOfMana = 0.4;
      }
    }
    this.outOfMana = Math.max(0, (this.outOfMana || 0) - dt);

    // procedural melee swing of the hand weapon
    if (this.weaponMount) {
      if (this.swing > 0 && this.weapon.wtype === "melee") {
        const p = 1 - this.swing / SWING_TIME;
        this.weaponMount.rotation.x = -Math.sin(p * Math.PI) * 1.9;
      } else {
        this.weaponMount.rotation.x = 0;
      }
    }

    const turn = Math.min(1, dt * 18);
    this.yaw = angleLerp(this.yaw, this.desiredYaw, turn);
    // ride the floor height (stairs/stories); ease so steps glide instead of pop
    if (level && level.groundY) {
      const gy = level.groundY(this.pos.x, this.pos.z);
      this.groundH += (gy - this.groundH) * Math.min(1, dt * 10);
    }
    this.pos.y = this.groundH;
    const hop = this.rollTimer > 0 ? Math.abs(Math.sin((this.rollTimer / GAME.rollTime) * Math.PI)) * 0.4 : 0;
    this.mesh.position.set(this.pos.x, this.groundH + hop, this.pos.z);
    this.mesh.rotation.y = this.yaw;

    if (this._bodyMat) {
      if (this.hurt > 0) { this._bodyMat.emissive = new THREE.Color(0x661111); this._bodyMat.emissiveIntensity = this.hurt * 4; }
      else this._bodyMat.emissiveIntensity = 0;
    }
  }

  takeHit(dmg, effects) {
    if ((this.invuln || 0) > 0) return false;   // grace period (e.g. just revived)
    if (this.rollTimer > 0) { if (effects) effects.text(this.pos, "dodge", "#9fd0ff"); return false; }   // i-frames while rolling
    const def = (this.armor ? this.armor.def : 0) + (this.armor ? memArmorBonus(this.armor).def : 0);
    const shielded = (this.shieldTimer || 0) > 0 ? dmg * 0.4 : dmg;   // Bulwark shield halves+ incoming
    const taken = Math.max(1, Math.round(shielded - def));
    this.hp -= taken;
    this.hurt = 0.25;
    if (effects) effects.text(this.pos, `-${taken}`, "#ff6b6b");
    if (this.hp < 0) this.hp = 0;
    return this.hp <= 0;
  }
}
